import { Command as Cmdk, useCommandState } from 'cmdk';
import { Bookmark, CirclePause, CircleStop, CornerDownLeft, ListFilter, Play, RotateCcw, Trash2, Users, X, Zap } from '@orgus/ui/icons';
import type { ComponentType, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Badge, Kbd, RISK_HINT, RiskBadge, StatusDot, cn, formatCompact, formatInt } from '@orgus/ui';
import { guardFor, performAct } from '../act/run.ts';
import { clauseChip } from '../ask/chips.ts';
import { formatUsd } from '../ask/evaluate.ts';
import type { AskGroup } from '../ask/evaluate.ts';
import { useViews, viewHref } from '../views/store.ts';
import { itemValue } from './match.ts';
import type { Power } from './usePower.ts';

const HEADING = '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-caption [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-fg-subtle';
const ITEM = 'group flex cursor-default select-none items-center gap-3 rounded-control px-2 text-ui text-fg-muted data-[selected=true]:bg-hover data-[selected=true]:text-fg';

const VERB_ICON: Record<string, ComponentType<{ className?: string }>> = {
  Stop: CircleStop,
  Pause: CirclePause,
  Resume: Play,
  Restart: RotateCcw,
  Remove: Trash2,
  Cancel: X,
};

const EXAMPLES = ['sessions failed today', 'machines offline', 'spend by project this week', 'top 5 sessions by tokens', 'audit failed by owner', 'worktrees dirty'];

function groupValue(g: AskGroup): string {
  if (g.value == null) return '';
  return g.valueKind === 'usd' ? formatUsd(g.value) : `${formatCompact(g.value)} tokens`;
}

/** Answer, actions and saved views, ahead of the palette's own groups. */
export function PowerGroups({ power, run, onExample }: { power: Power; run: (fn: () => void | Promise<void>) => void; onExample: (q: string) => void }) {
  const navigate = useNavigate();
  const mine = useViews((s) => s.mine);
  const team = useViews((s) => s.team);
  const { answer, acts, mode } = power;

  return (
    <>
      {mode === 'ask' && !power.text && (
        <Cmdk.Group heading="Ask about live state" className={HEADING} forceMount>
          {EXAMPLES.map((ex, i) => (
            <Cmdk.Item key={ex} value={`ask:${i}:example`} forceMount className={cn(ITEM, 'h-menu-item')} onSelect={() => onExample(`? ${ex}`)}>
              <ListFilter className="size-4 text-fg-subtle" aria-hidden />
              <span className="text-fg-muted">{ex}</span>
            </Cmdk.Item>
          ))}
        </Cmdk.Group>
      )}

      {answer && power.text && (
        // Keyed by the question, so each answer mounts fresh in its own order.
        <Cmdk.Group key={`answer:${power.text}:${answer.total}`} heading="Answer" className={HEADING} forceMount>
          <Cmdk.Item value="ask:0:summary" forceMount onSelect={() => power.openAnswer(answer)} className={cn(ITEM, 'items-start py-3')}>
            <ListFilter className="mt-1 size-4 shrink-0 text-accent" aria-hidden />
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-body font-medium text-fg num">{answer.summary}</span>
              <span className="flex flex-wrap items-center gap-1">
                {answer.query.clauses.map((c, i) => {
                  const chip = clauseChip(c);
                  return (
                    <Badge key={i} tone="neutral" variant="outline">
                      <span className="text-fg-subtle">{chip.field}</span>&nbsp;{chip.value}
                    </Badge>
                  );
                })}
                {answer.query.groupBy && (
                  <Badge tone="neutral" variant="outline">
                    <span className="text-fg-subtle">By</span>&nbsp;{answer.query.groupBy}
                  </Badge>
                )}
              </span>
              {answer.note && <span className="text-caption text-fg-subtle">{answer.note}</span>}
            </span>
            <span className="flex shrink-0 flex-col items-end gap-1 text-caption text-fg-subtle">
              <span className="flex items-center gap-1">
                Open as view <Kbd keys={['enter']} />
              </span>
              <span className="flex items-center gap-1">
                Save view <Kbd keys={['mod', 's']} />
              </span>
            </span>
          </Cmdk.Item>
          {answer.groups?.slice(0, 8).map((g, i) => (
            <Cmdk.Item
              key={`g-${g.key}`}
              value={`ask:${i + 1}:group`}
              forceMount
              className={cn(ITEM, 'h-menu-item')}
              onSelect={() => {
                const field = answer.query.groupBy;
                const clause = field === 'project' && g.key ? `project:${JSON.stringify(g.label)}` : field === 'machine' ? `on ${JSON.stringify(g.label)}` : '';
                const where = [answer.view.params.where, clause].filter(Boolean).join(' ');
                power.openAnswer(answer, where ? { where } : {});
              }}
            >
              <span className="flex size-4 shrink-0 items-center justify-center text-fg-subtle">
                <span className="size-1.5 rounded-full bg-fg-subtle" />
              </span>
              <span className="min-w-0 flex-1 truncate text-fg">{g.label}</span>
              <span className="text-caption text-fg-subtle num">{formatInt(g.count)}</span>
              <span className="w-24 text-right text-caption font-medium text-fg num">{groupValue(g)}</span>
            </Cmdk.Item>
          ))}
          {!answer.groups &&
            answer.rows.map((r, i) => (
              <Cmdk.Item key={`r-${r.id}`} value={`ask:${i + 1}:row`} forceMount className={cn(ITEM, 'h-menu-item')} onSelect={() => run(() => navigate(r.href))}>
                <span className="flex size-4 shrink-0 items-center justify-center">{r.tone ? <StatusDot tone={r.tone} size="sm" label="" /> : null}</span>
                <span className="min-w-0 flex-1 truncate">
                  <span className="text-fg">{r.title}</span>
                  <span className="ml-2 text-fg-subtle">{r.subtitle}</span>
                </span>
                <CornerDownLeft className="hidden size-3.5 text-fg-subtle group-data-[selected=true]:block" aria-hidden />
              </Cmdk.Item>
            ))}
          {!answer.groups && answer.total > answer.rows.length && (
            <Cmdk.Item value={`ask:9:more`} forceMount className={cn(ITEM, 'h-menu-item text-caption')} onSelect={() => power.openAnswer(answer)}>
              <span className="size-4" />
              <span className="text-fg-subtle">and {formatInt(answer.total - answer.rows.length)} more in the list</span>
            </Cmdk.Item>
          )}
        </Cmdk.Group>
      )}

      {acts.length > 0 && (
        <Cmdk.Group key={`acts:${power.text}`} heading={mode === 'act' ? 'Actions' : power.text ? 'Do it' : 'Suggested here'} className={HEADING} forceMount>
          {acts.map((a, i) => {
            const Icon = VERB_ICON[a.verb] ?? Zap;
            return (
              <Cmdk.Item key={a.id} value={`act:${i}`} keywords={[a.title, ...a.keywords]} forceMount className={cn(ITEM, 'min-h-row py-1')} onSelect={() => run(() => performAct(a))}>
                <Icon className={cn('size-4 shrink-0', a.risk === 'high' ? 'text-failed' : 'text-fg-subtle')} aria-hidden />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-fg">{a.title}</span>
                  <span className="truncate text-caption text-fg-subtle">{a.scopeLabel}</span>
                </span>
                <RiskBadge risk={a.risk} compact />
              </Cmdk.Item>
            );
          })}
        </Cmdk.Group>
      )}

      {mode === 'mixed' && power.text && (mine.length > 0 || team.length > 0) && (
        <Cmdk.Group heading="Saved views" className={HEADING}>
          {[...mine, ...team].map((v) => (
            <Cmdk.Item key={v.id} value={itemValue(v.name, `view:${v.id}`)} keywords={[v.path.slice(1), 'view']} className={cn(ITEM, 'h-menu-item')} onSelect={() => run(() => navigate(viewHref(v)))}>
              {v.shared ? <Users className="size-4 text-fg-subtle" aria-hidden /> : <Bookmark className="size-4 text-fg-subtle" aria-hidden />}
              <span className="min-w-0 flex-1 truncate">
                <span className="text-fg">{v.name}</span>
                <span className="ml-2 text-fg-subtle">{v.path.slice(1)}</span>
              </span>
            </Cmdk.Item>
          ))}
        </Cmdk.Group>
      )}
    </>
  );
}

/** Under the list: the selected action's scope and risk, before Enter. */
export function ActPreview({ power }: { power: Power }): ReactNode {
  const selected = useCommandState((s) => s.value);
  if (!selected?.startsWith('act:')) return null;
  const a = power.acts[Number(selected.split(':')[1] ?? -1)];
  if (!a) return null;
  const route = guardFor(a);
  return (
    <div className="flex items-start gap-3 border-t border-border-subtle bg-sunken px-4 py-2 text-caption" aria-live="polite">
      <RiskBadge risk={a.risk} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-fg">
          <span className="text-fg-subtle">Affects</span> {a.scopeLabel}
        </p>
        <p className="text-fg-subtle">
          {RISK_HINT[a.risk]}
          {route === 'undo' ? ' Undo stays in the toast for 6 s.' : ''}
        </p>
      </div>
      <span className="flex shrink-0 items-center gap-1 text-caption text-fg-subtle">
        {route === 'step-up' || route === 'confirm' ? 'Review' : 'Run'} <Kbd keys={['enter']} />
      </span>
    </div>
  );
}

